/**
 * Issue Intelligence Engine
 *
 * Auto-generates structured, actionable issue descriptions from system data.
 * Every issue gets: What is wrong, Stage, Blocker, Homeowner Concern, Action Required, Action Links.
 */

import type { Job, Issue } from "./types";
import type { Quote } from "./quoteTypes";
import type { Contractor } from "./types";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";

export interface ActionLink {
  label: string;
  path: string;
  icon: "dispatch" | "job" | "materials" | "permit" | "schedule" | "qa" | "inventory" | "issues" | "contractor";
}

export interface StructuredIssueDetail {
  /** Primary title: "Customer – City – Issue Type" */
  primaryTitle: string;
  /** Secondary line: street address \n phone \n job number */
  secondaryLine: string;
  /** Street address for display */
  streetAddress: string;
  /** City name */
  city: string;
  /** What is wrong — specific, not generic */
  issue: string;
  /** What stage the job/entity is stuck in */
  stage: string;
  /** What is blocking progress — NEVER vague */
  blocker: string;
  /** Why this matters / risk impact */
  impact: string;
  /** What the homeowner reported or is experiencing */
  homeownerConcern: string;
  /** Clear next step for resolution */
  actionRequired: string;
  /** Deep-link action buttons */
  actionLinks: ActionLink[];
  /** Validated severity — downgraded if blocker missing */
  validatedSeverity: "critical" | "warning";
}

function extractCity(address: string): string {
  if (!address) return "Unknown";
  const parts = address.split(",").map((p) => p.trim());
  // Typical format: "123 Main St, City, FL 34236"
  // City is usually the second-to-last part, but skip state/zip
  if (parts.length >= 3) {
    // Last part is state+zip, second-to-last is city
    return parts[parts.length - 2] || parts[0];
  }
  if (parts.length === 2) return parts[1] || parts[0];
  return parts[0] || "Unknown";
}

function extractStreetAddress(address: string): string {
  if (!address) return "";
  const parts = address.split(",").map((p) => p.trim());
  return parts[0] || "";
}

function formatPhone(phone: string): string {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return phone;
}

/** Generate structured detail for a job-based attention item */
export function getJobIssueDetail(
  job: Job,
  itemReason: string,
  openIssues: Issue[]
): StructuredIssueDetail {
  const city = job.address.city || "Unknown";
  const street = job.address.street || "";
  const phone = formatPhone(job.phone);

  const issueType = deriveJobIssueType(job, openIssues);
  const primaryTitle = `${job.customerName} – ${city} – ${issueType}`;
  const secondaryLine = [
    street,
    phone,
    `Job #${job.jobNumber || job.id}`,
  ].filter(Boolean).join(" • ");

  const stage = deriveJobStage(job);
  const blocker = deriveJobBlocker(job, openIssues);
  const actionRequired = deriveJobAction(job, openIssues);
  const issue = deriveJobIssueDescription(job, openIssues, itemReason);
  const homeownerConcern = deriveJobHomeownerConcern(job, openIssues);
  const impact = deriveJobImpact(job, openIssues);
  const actionLinks = deriveJobActionLinks(job, openIssues);
  const validatedSeverity = validateSeverity(blocker, openIssues);

  return { primaryTitle, secondaryLine, streetAddress: street, city, issue, stage, blocker, impact, homeownerConcern, actionRequired, actionLinks, validatedSeverity };
}

/** Generate structured detail for a system issue entity */
export function getIssueEntityDetail(
  issue: Issue,
  job: Job | undefined
): StructuredIssueDetail {
  const customerName = job?.customerName || "Unknown Customer";
  const city = job ? (job.address.city || "Unknown") : "Unknown";
  const street = job ? (job.address.street || "") : "";
  const phone = job ? formatPhone(job.phone) : "";

  const primaryTitle = `${customerName} – ${city} – ${issue.issueType}`;
  const secondaryLine = [
    street,
    phone,
    `Job #${issue.jobNumber || issue.jobId}`,
  ].filter(Boolean).join(" • ");

  const stage = issue.jobStageAtCreation || (job ? deriveJobStage(job) : "Unknown");

  // Blocker: prioritize structured data, never return vague text
  let blocker: string;
  if (issue.blockerDetail) {
    blocker = issue.blockerDetail;
  } else if (issue.issueType === "Material Issue" && job?.materialsList) {
    const missing = job.materialsList.filter((m) => m.missingItems > 0);
    if (missing.length > 0) {
      blocker = missing.map((m) => `${m.itemName} (SKU: ${m.sku}) — need ${m.quantityRequired}, have ${m.quantityAllocated}`).join("; ");
    } else {
      blocker = `Material issue reported: ${issue.description.slice(0, 120)}`;
    }
  } else if (issue.issueType === "Permit Issue" && job?.permitDetail) {
    blocker = job.permitDetail.blockers || `Permit ${job.permitDetail.detailedStage} at ${job.permitDetail.municipality}`;
  } else {
    blocker = issue.description || "Awaiting field input — assign owner to investigate and define blocker";
  }

  // Action: prioritize structured data
  const actionRequired = issue.suggestedAction || deriveIssueAction(issue, job);

  // Homeowner concern
  const homeownerConcern = issue.homeownerConcern || deriveIssueHomeownerConcern(issue);

  // Impact
  const impact = deriveIssueImpact(issue, job);

  // Action links
  const actionLinks = deriveIssueActionLinks(issue, job);

  // Validated severity
  const validatedSeverity = issue.blockerDetail || issue.severity === "Urgent"
    ? (issue.severity === "Urgent" ? "critical" : "warning")
    : "warning"; // Downgrade if no blocker defined

  return {
    primaryTitle,
    secondaryLine,
    streetAddress: street,
    city,
    issue: issue.description,
    stage,
    blocker,
    impact,
    homeownerConcern,
    actionRequired,
    actionLinks,
    validatedSeverity,
  };
}

/** Generate structured detail for a quote attention item */
export function getQuoteIssueDetail(
  quote: Quote,
  reason: string
): StructuredIssueDetail {
  const city = (quote as any).projectAddress ? extractCity((quote as any).projectAddress) : "Unknown";
  const street = (quote as any).projectAddress ? extractStreetAddress((quote as any).projectAddress) : "";
  const primaryTitle = `${quote.customerName} – ${city} – Stale Quote`;
  const secondaryLine = [
    street,
    `Quote #${quote.quote_number || quote.id}`,
    quote.status,
    quote.totalEstimate > 0 ? `$${quote.totalEstimate.toLocaleString()}` : "",
  ].filter(Boolean).join(" • ");

  return {
    primaryTitle,
    secondaryLine,
    streetAddress: street,
    city,
    issue: `Quote has been in "${quote.status}" status with no activity`,
    stage: "Sales / Negotiation",
    blocker: reason || "No customer engagement or follow-up recorded since last activity",
    impact: `$${quote.totalEstimate.toLocaleString()} potential revenue at risk if quote goes cold`,
    homeownerConcern: "Customer may be waiting for follow-up or evaluating competing quotes",
    actionRequired: "Follow up with customer to advance or close the quote. Schedule a callback or send a follow-up message.",
    actionLinks: [
      { label: "Open Quote", path: `/opportunities/${quote.id}`, icon: "job" },
    ],
    validatedSeverity: "warning",
  };
}

/** Generate structured detail for a contractor attention item */
export function getContractorIssueDetail(
  contractor: Contractor,
  openIssueCount: number,
  creditCount: number
): StructuredIssueDetail {
  const primaryTitle = `${contractor.companyName} – Performance Risk`;
  const secondaryLine = [
    contractor.contactName,
    contractor.phone,
    Array.isArray(contractor.serviceArea) ? contractor.serviceArea.join(", ") : (contractor.serviceArea || ""),
  ].filter(Boolean).join(" • ");

  return {
    primaryTitle,
    secondaryLine,
    streetAddress: "",
    city: "",
    issue: `Contractor has ${openIssueCount} open issues and ${creditCount} customer credits linked to their work`,
    stage: "Contractor Management",
    blocker: openIssueCount >= 3
      ? "Pattern of recurring quality or reliability issues across multiple jobs"
      : creditCount > 0
      ? `${creditCount} customer credit(s) issued due to quality concerns`
      : "Performance metrics below acceptable threshold",
    impact: openIssueCount >= 3
      ? "Risk of customer escalation, negative reviews, and operational delays across multiple active jobs"
      : "Quality issues may lead to additional credits and customer dissatisfaction",
    homeownerConcern: openIssueCount >= 3
      ? "Multiple customers affected — potential for negative reviews or escalations"
      : "Customers have reported quality issues requiring credits",
    actionRequired: openIssueCount >= 3
      ? "Schedule a performance review meeting. Consider pausing new assignments until issues are resolved."
      : "Review credit history and discuss quality improvement expectations.",
    actionLinks: [
      { label: "View Contractor", path: `/contractors/${contractor.id}`, icon: "contractor" },
      { label: "View Issues", path: "/issues", icon: "issues" },
    ],
    validatedSeverity: openIssueCount >= 3 ? "critical" : "warning",
  };
}

// ─── Impact Derivation ───

function deriveJobImpact(job: Job, openIssues: Issue[]): string {
  if (job.scheduledInstallDate) {
    const daysUntil = (new Date(job.scheduledInstallDate).getTime() - Date.now()) / 86_400_000;
    if (daysUntil >= 0 && daysUntil <= 5) {
      return `Installation scheduled in ${Math.ceil(daysUntil)} day(s) — delays will impact customer schedule and crew allocation`;
    }
  }
  if (job.financials?.paymentStatus === "Overdue") {
    return `$${getJobDisplayValue(job).toLocaleString()} outstanding — cash flow impact and potential customer relationship damage`;
  }
  if (job.materialReadiness === "Missing Items") {
    return "Installation cannot proceed until materials are fulfilled — risk of schedule slip and customer delay notification";
  }
  if (job.permitRequired && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required") {
    return "Job cannot advance past permitting stage — all downstream scheduling is blocked";
  }
  if (openIssues.some((i) => i.severity === "Urgent")) {
    return "Urgent issue(s) present — risk of escalation and customer complaint if not addressed promptly";
  }
  if (job.assignmentType === "Unassigned") {
    return "No crew assigned — job cannot proceed to installation phase";
  }
  return "Operational delay risk — review and advance to maintain project timeline";
}

function deriveIssueImpact(issue: Issue, job: Job | undefined): string {
  if (job?.scheduledInstallDate) {
    const daysUntil = (new Date(job.scheduledInstallDate).getTime() - Date.now()) / 86_400_000;
    if (daysUntil >= 0 && daysUntil <= 5) {
      return `Install in ${Math.ceil(daysUntil)} day(s) — unresolved issue may force schedule change`;
    }
  }
  switch (issue.issueType) {
    case "Customer Complaint":
      return "Customer satisfaction at risk — delayed response increases escalation likelihood";
    case "Material Issue":
      return "Material gap may delay installation — review alternatives or expedite order";
    case "Permit Issue":
      return "Permit block prevents all downstream work — job timeline at risk";
    case "Install Issue":
      return "Quality issue may require rework — customer may escalate if not addressed";
    case "Scheduling Issue":
      return "Schedule disruption affects crew utilization and customer expectations";
    case "Contractor Issue":
      return "Contractor reliability concern — may need reassignment to protect timeline";
    default:
      return "Operational risk — review and resolve to maintain project momentum";
  }
}

// ─── Action Links ───

function deriveJobActionLinks(job: Job, openIssues: Issue[]): ActionLink[] {
  const links: ActionLink[] = [];

  if (job.assignmentType === "Unassigned") {
    links.push({ label: "Assign Crew", path: `/scheduling`, icon: "dispatch" });
  }
  if (job.materialReadiness === "Missing Items") {
    links.push({ label: "Review Materials", path: `/inventory`, icon: "materials" });
  }
  if (job.permitRequired && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required") {
    links.push({ label: "Open Permitting", path: `/permitting`, icon: "permit" });
  }
  if (openIssues.length > 0) {
    links.push({ label: "View Issues", path: `/issues`, icon: "issues" });
  }
  if (job.financials?.paymentStatus === "Overdue") {
    links.push({ label: "View Accounting", path: `/accounting`, icon: "job" });
  }
  if (job.currentStage === "QA Review") {
    links.push({ label: "Open QA", path: `/qa`, icon: "qa" });
  }

  // Always include View Job
  links.push({ label: "View Job", path: `/jobs/${job.id}`, icon: "job" });

  return links;
}

function deriveIssueActionLinks(issue: Issue, job: Job | undefined): ActionLink[] {
  const links: ActionLink[] = [];

  switch (issue.issueType) {
    case "Material Issue":
    case "Ordering Issue":
      links.push({ label: "Review Inventory", path: "/inventory", icon: "inventory" });
      links.push({ label: "Open Procurement", path: "/procurement", icon: "materials" });
      break;
    case "Permit Issue":
      links.push({ label: "Open Permitting", path: "/permitting", icon: "permit" });
      break;
    case "Install Issue":
      links.push({ label: "Open QA Module", path: "/qa", icon: "qa" });
      break;
    case "Customer Complaint":
      links.push({ label: "View Service Tickets", path: "/service-tickets", icon: "issues" });
      break;
    case "Scheduling Issue":
      links.push({ label: "Open Scheduling", path: "/scheduling", icon: "schedule" });
      break;
    case "Contractor Issue":
      if (issue.relatedContractorId) {
        links.push({ label: "View Contractor", path: `/contractors/${issue.relatedContractorId}`, icon: "contractor" });
      }
      links.push({ label: "View Contractors", path: "/contractors", icon: "contractor" });
      break;
    default:
      links.push({ label: "View Issues", path: "/issues", icon: "issues" });
  }

  if (job) {
    links.push({ label: "View Job", path: `/jobs/${job.id}`, icon: "job" });
  }

  return links;
}

// ─── Severity Validation ───

function validateSeverity(blocker: string, openIssues: Issue[]): "critical" | "warning" {
  // If blocker is vague/pending, cannot be critical
  const vaguePatterns = ["awaiting field input", "pending identification", "review record"];
  const isVague = vaguePatterns.some((p) => blocker.toLowerCase().includes(p));
  if (isVague) return "warning";

  // If there are urgent issues with defined blockers, it's critical
  const urgentWithBlocker = openIssues.filter((i) => i.severity === "Urgent" && i.blockerDetail);
  if (urgentWithBlocker.length > 0) return "critical";

  return "warning";
}

// ─── Internal Helpers ───

function deriveJobIssueType(job: Job, openIssues: Issue[]): string {
  if (job.materialReadiness === "Missing Items") return "Missing Inventory";
  if (job.permitRequired && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required") return "Permit Hold";
  if (job.financials?.paymentStatus === "Overdue") return "Overdue Payment";
  if (job.currentStage === "Completed" && job.financials?.paymentStatus !== "Paid") return "Unpaid Completion";
  if (openIssues.length > 0) return openIssues[0].issueType;
  if (job.assignmentType === "Unassigned") return "No Crew Assigned";
  if (job.dispatchStatus === "Not Ready" || job.dispatchStatus === "Ready for Dispatch") return "Dispatch Delay";
  return "Operational Review Required";
}

function deriveJobStage(job: Job): string {
  const stageMap: Record<string, string> = {
    "Sale Closed": "Post-Sale / Pre-Production",
    "Review and Validation": "Review & Validation",
    "Permit and Order Pending": "Permitting & Ordering",
    "Ready for Scheduling": "Scheduling",
    "Scheduled Tentative": "Scheduling (Tentative)",
    "Scheduled Confirmed": "Pre-Install",
    "Ready for Dispatch": "Pre-Install (Dispatch Ready)",
    "Available for Contractor Assignment": "Contractor Assignment",
    "Assigned and Dispatched": "Dispatched",
    "In Progress": "Installation",
    "QA Review": "Quality Assurance",
    "Completed": "Post-Install / Closeout",
  };
  return stageMap[job.currentStage] || job.currentStage;
}

function deriveJobBlocker(job: Job, openIssues: Issue[]): string {
  const blockers: string[] = [];

  if (job.materialReadiness === "Missing Items") {
    if (job.materialsList) {
      const missing = job.materialsList.filter((m) => m.missingItems > 0);
      if (missing.length > 0) {
        blockers.push(missing.map((m) => `${m.itemName} (SKU: ${m.sku}) — need ${m.quantityRequired}, have ${m.quantityAllocated}`).join("; "));
      } else {
        blockers.push("Material readiness flagged — verify staging status in warehouse system");
      }
    } else {
      blockers.push("Materials marked as missing — inventory data unavailable, warehouse verification required");
    }
  }

  if (job.permitRequired && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required") {
    if (job.permitDetail) {
      blockers.push(`Permit ${job.permitDetail.detailedStage} at ${job.permitDetail.municipality}${job.permitDetail.blockers ? ` — ${job.permitDetail.blockers}` : ""}`);
    } else {
      blockers.push(`Permit status: ${job.permitStatus || "Pending"} — follow up with permitting coordinator`);
    }
  }

  if (openIssues.length > 0) {
    const withBlockers = openIssues.filter((i) => i.blockerDetail);
    if (withBlockers.length > 0) {
      blockers.push(withBlockers.map((i) => `${i.issueType}: ${i.blockerDetail}`).join(" | "));
    } else {
      const urgent = openIssues.filter((i) => i.severity === "Urgent");
      if (urgent.length > 0) {
        blockers.push(`${urgent.length} urgent issue(s): ${urgent.map((i) => `${i.issueType} — ${i.description.slice(0, 60)}`).join("; ")}`);
      } else {
        blockers.push(`${openIssues.length} open issue(s): ${openIssues.map((i) => `${i.issueType} — ${i.description.slice(0, 60)}`).join("; ")}`);
      }
    }
  }

  if (job.assignmentType === "Unassigned") {
    blockers.push("No crew or subcontractor assigned — job cannot proceed to installation");
  }

  if (job.financials?.paymentStatus === "Overdue") {
    blockers.push(`Payment overdue — contract value $${getJobDisplayValue(job).toLocaleString()}`);
  }

  if (blockers.length === 0) {
    // Derive from current stage to avoid vague text
    if (job.dispatchStatus === "Not Ready") {
      return "Dispatch prerequisites not met — verify materials, permit, and crew assignment status";
    }
    if (job.currentStage === "Ready for Scheduling") {
      return "Job ready but not scheduled — assign install date and crew";
    }
    if (job.currentStage === "Available for Contractor Assignment") {
      return "Job awaiting contractor assignment — send to subcontractor network or assign internal crew";
    }
    return "Awaiting field input — assign owner to investigate and define specific blocker";
  }

  return blockers.join(" | ");
}

function deriveJobAction(job: Job, openIssues: Issue[]): string {
  const actions: string[] = [];

  const structuredActions = openIssues.filter((i) => i.suggestedAction).map((i) => i.suggestedAction!);
  if (structuredActions.length > 0) {
    actions.push(...structuredActions.slice(0, 2));
  }

  if (job.materialReadiness === "Missing Items") {
    actions.push("Assign inventory or reorder missing materials before scheduling install");
  }
  if (job.permitRequired && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required") {
    if (job.permitStatus === "Revision Needed" || job.permitDetail?.detailedStage === "Revision Requested") {
      actions.push("Revise and resubmit permit application to municipality");
    } else {
      actions.push("Follow up with municipality on permit status");
    }
  }
  if (openIssues.some((i) => i.severity === "Urgent") && structuredActions.length === 0) {
    actions.push("Resolve urgent issues before proceeding with installation");
  }
  if (job.assignmentType === "Unassigned") {
    actions.push("Assign crew or dispatch to subcontractor network");
  }
  if (job.financials?.paymentStatus === "Overdue") {
    actions.push("Contact customer to collect outstanding payment");
  }
  if (job.currentStage === "Completed" && job.financials?.paymentStatus !== "Paid") {
    actions.push("Initiate final billing and collect remaining balance");
  }

  const unique = [...new Set(actions)];
  return unique.length > 0 ? unique.join(". ") + "." : "Review job status and advance to next operational step.";
}

function deriveJobIssueDescription(job: Job, openIssues: Issue[], fallbackReason: string): string {
  if (job.materialReadiness === "Missing Items") {
    const missing = job.materialsList?.filter((m) => m.missingItems > 0);
    if (missing && missing.length > 0) {
      return `${missing.length} material item(s) not allocated — installation cannot proceed until inventory is fulfilled`;
    }
    return "Materials flagged as missing — job cannot proceed to install";
  }
  if (job.permitRequired && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required") {
    return `Permit has not been approved — job is blocked at the permitting stage`;
  }
  if (job.financials?.paymentStatus === "Overdue") {
    return "Customer payment is overdue — financial hold on job progression";
  }
  if (job.currentStage === "Completed" && job.financials?.paymentStatus !== "Paid") {
    return "Job completed but final payment has not been collected";
  }
  if (openIssues.length > 0) {
    const primary = openIssues[0];
    return primary.description || `${openIssues.length} unresolved issue(s) affecting job progress`;
  }
  if (job.assignmentType === "Unassigned") {
    return "No crew or subcontractor assigned — job is stalled at assignment stage";
  }
  if (job.dispatchStatus === "Not Ready") {
    return "Dispatch prerequisites incomplete — review readiness checklist before scheduling";
  }
  return fallbackReason || "Job flagged for operational review — see blocker details above";
}

function deriveJobHomeownerConcern(job: Job, openIssues: Issue[]): string {
  const withConcerns = openIssues.filter((i) => i.homeownerConcern);
  if (withConcerns.length > 0) {
    return withConcerns.map((i) => i.homeownerConcern!).join("; ");
  }
  const complaints = openIssues.filter((i) => i.issueType === "Customer Complaint");
  if (complaints.length > 0) {
    return complaints.map((i) => i.description.slice(0, 100)).join("; ");
  }
  if (job.financials?.paymentStatus === "Overdue") {
    return "Customer may be unaware of outstanding balance or disputing charges";
  }
  if (job.currentStage === "Completed" && job.financials?.paymentStatus !== "Paid") {
    return "Customer may expect project is fully closed — follow up on payment";
  }
  return "No direct homeowner concern reported at this time";
}

function deriveIssueHomeownerConcern(issue: Issue): string {
  if (issue.issueType === "Customer Complaint") return issue.description;
  if (issue.issueType === "Install Issue") return "Customer may notice quality issue during or after installation";
  if (issue.issueType === "Material Issue") return "Material delay or substitution may affect customer expectations";
  if (issue.issueType === "Scheduling Issue") return "Customer schedule may be impacted by scheduling changes";
  return "No direct homeowner concern reported at this time";
}

function deriveIssueAction(issue: Issue, job: Job | undefined): string {
  switch (issue.issueType) {
    case "Material Issue": return "Verify inventory allocation in warehouse. Reorder if needed and update job material list.";
    case "Permit Issue": return "Contact municipality to clarify permit requirements. Revise and resubmit if revision was requested.";
    case "Install Issue": return "Dispatch field supervisor to assess and document the issue. Schedule corrective work if needed.";
    case "Customer Complaint": return "Contact customer within 24 hours to acknowledge and discuss resolution options.";
    case "Scheduling Issue": return "Review scheduling calendar for conflicts. Reassign crew or reschedule as needed.";
    case "Ordering Issue": return "Check order status with supplier. Expedite or source alternative materials.";
    case "Contractor Issue": return "Review contractor performance. Consider reassignment if pattern continues.";
    default: return "Review issue details and determine appropriate resolution path.";
  }
}
